import { parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import type { useGT } from 'gt-react';
import type {
  ArTaskRow,
  ArTasksAgingBucket,
  ArTasksSummaryResponse,
  ArTasksTab,
  ListArTasksInput,
} from '@erp/rpc';
import { AR_TASK_TYPE_KEY_BY_KIND } from '@erp/rpc';
import { SystemEntity } from '@erp/shared-types';
import {
  AR_TASKS_DEFAULT_PAGE_SIZE,
  type ArTasksSearch,
} from './arTasksSearch';

/** The billing record behind a task; the labels come only from a list row. */
export type ArTaskRecord = Pick<ArTaskRow, 'kind'> & {
  anchorId: string;
} & Partial<Pick<ArTaskRow, 'reference' | 'customerId' | 'customerName'>>;

const KIND_BY_ANCHOR: Partial<Record<string, ArTaskRow['kind']>> = {
  [SystemEntity.AR_INVOICE]: 'to_send',
  [SystemEntity.AR_SALES_ORDER]: 'to_invoice',
};

/**
 * The record a task sheet links to. Prefers the list row (it has the
 * reference and customer), else falls back to the task's own anchor, so a
 * bookmarked task or one that left the current page still links its record.
 * Null while the record hasn't synced into Gently.
 */
export function arTaskRecord(
  row: ArTaskRow | undefined,
  task:
    | { anchorEntityType: string | null; anchorEntityId: string | null }
    | undefined,
): ArTaskRecord | null {
  if (row) {
    const { anchorId } = row;
    return anchorId ? { ...row, anchorId } : null;
  }
  const kind = KIND_BY_ANCHOR[task?.anchorEntityType ?? ''];
  if (!kind || !task?.anchorEntityId) return null;
  return { kind, anchorId: task.anchorEntityId };
}

const KIND_BY_TASK_TYPE_KEY: Partial<Record<string, ArTaskRow['kind']>> = {
  [AR_TASK_TYPE_KEY_BY_KIND.to_send]: 'to_send',
  [AR_TASK_TYPE_KEY_BY_KIND.to_invoice]: 'to_invoice',
};

/**
 * A task's kind from its type, for a task with neither a list row nor a
 * synced record (e.g. a bookmarked one whose record isn't in Gently yet).
 */
export function arTaskKindOfType(
  taskTypeKey: string | undefined,
): ArTaskRow['kind'] | undefined {
  return KIND_BY_TASK_TYPE_KEY[taskTypeKey ?? ''];
}

/**
 * The open task's list row: the current page's, else the last one seen for
 * it, so paging or filtering away keeps its channels and ERP note in the
 * detail. Undefined when it hasn't been on a page yet (e.g. a bookmark).
 */
export function openTaskRow(
  rows: readonly ArTaskRow[],
  taskId: string | null,
  remembered: ArTaskRow | undefined,
): ArTaskRow | undefined {
  if (!taskId) return undefined;
  return (
    rows.find((row) => row.taskId === taskId) ??
    (remembered?.taskId === taskId ? remembered : undefined)
  );
}

/** The record's number: the row's, else the loaded invoice's or order's. */
export function recordReference(
  record: ArTaskRecord | null,
  loaded: { invoiceNumber?: string | null; originId?: string | null },
): string | null {
  return record?.reference ?? loaded.invoiceNumber ?? loaded.originId ?? null;
}

/**
 * URL search → list query input, with defaults for tab and paging. A sort
 * goes through only when the URL has both its column and direction (the
 * header shows no sort arrow otherwise), and tie-breakers only with it.
 */
export function buildArTasksListInput(search: ArTasksSearch): ListArTasksInput {
  const sorted = Boolean(search.sortBy && search.sortDirection);
  return {
    tab: search.tab ?? 'all',
    search: search.search || undefined,
    assignedToMe: search.assignedToMe || undefined,
    agingBucket: search.aging,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
    page: search.page ?? 1,
    pageSize: search.pageSize ?? AR_TASKS_DEFAULT_PAGE_SIZE,
    groupBy: search.groupBy,
    sortBy: sorted ? search.sortBy : undefined,
    sortDirection: sorted ? search.sortDirection : undefined,
    thenSort: sorted ? search.thenSort : undefined,
  };
}

/** True when any filter beyond the tab is applied (drives the toolbar's Reset). */
export function hasArTasksFilters(search: ArTasksSearch): boolean {
  return Boolean(
    search.search ||
    search.assignedToMe ||
    search.aging ||
    search.dateFrom ||
    search.dateTo,
  );
}

/** Every filter cleared, for the toolbar's Reset. */
export const CLEARED_AR_TASKS_FILTERS: Partial<ArTasksSearch> = {
  search: undefined,
  assignedToMe: undefined,
  aging: undefined,
  dateFrom: undefined,
  dateTo: undefined,
};

/** URL dates → the DateRangePicker value; undefined when no date is set. */
export function arTasksDateRange(search: ArTasksSearch): DateRange | undefined {
  if (!search.dateFrom && !search.dateTo) return undefined;
  return {
    from: search.dateFrom ? parseISO(search.dateFrom) : undefined,
    to: search.dateTo ? parseISO(search.dateTo) : undefined,
  };
}

/** Clicking the active aging bucket clears it; any other bucket selects it. */
export function nextAgingBucket(
  current: ArTasksAgingBucket | undefined,
  picked: ArTasksAgingBucket,
): ArTasksAgingBucket | undefined {
  return current === picked ? undefined : picked;
}

/** Total pages for the paginator; zero when there are no rows. */
export function arTasksPageCount(total: number, pageSize: number): number {
  return total > 0 ? Math.ceil(total / pageSize) : 0;
}

/** Open-task count per tab from the summary; All is the sum of both kinds. */
export function arTasksTabCounts(
  summary: ArTasksSummaryResponse | undefined,
): Record<ArTasksTab, number> | undefined {
  if (!summary) return undefined;
  return {
    all: summary.toInvoice.count + summary.toSend.count,
    to_invoice: summary.toInvoice.count,
    to_send: summary.toSend.count,
  };
}

type Translate = ReturnType<typeof useGT>;

/** Each tab's label; the split view's rail header reuses it. */
export function arTasksTabLabel(tab: ArTasksTab, gt: Translate): string {
  const labels: Record<ArTasksTab, string> = {
    all: gt('All', { context: 'AR tasks tab, every task' }),
    to_invoice: gt('Shipped but not invoiced', {
      context: 'AR tasks tab, shipped orders with no invoice',
    }),
    to_send: gt('Invoiced but not sent', {
      context: 'AR tasks tab, invoices not sent',
    }),
  };
  return labels[tab];
}

/** The record slide-in a related record opens: an invoice or a sales order. */
export function recordOverlay(
  record: ArTaskRecord,
): { invoiceId: string } | { salesOrderId: string } {
  return record.kind === 'to_send'
    ? { invoiceId: record.anchorId }
    : { salesOrderId: record.anchorId };
}

const CLOSED_TASK_STATUSES: readonly string[] = [
  'completed',
  'failed',
  'cancelled',
];

/** Whether a task is still open (not completed, failed or cancelled). */
export function isOpenTaskStatus(status: string): boolean {
  return !CLOSED_TASK_STATUSES.includes(status);
}
